import { toast } from 'sonner';

import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/lib/logger';

/**
 * Extracts bucket and path from a Supabase storage URL.
 * Handles both /object/public/ and /object/authenticated/ formats.
 */
function parseStorageUrl(url: string): { bucket: string; path: string } | null {
    try {
        const match = url.match(/\/storage\/v1\/object\/(?:public|authenticated)\/([^/]+)\/(.+)/);
        if (match) {
            return { bucket: match[1], path: decodeURIComponent(match[2]) };
        }
    } catch {
        // ignore parse errors
    }
    return null;
}

/**
 * The one sentence a driver gets when a stored file will not open.
 *
 * ── IT USED TO FAIL IN SILENCE, AND THAT IS THE BUG ───────────────────────────
 * A refused `createSignedUrl` was reported to Sentry and NOTHING happened on screen: the
 * button simply did not respond, which a driver reads as a broken app rather than as a file
 * they cannot have. That was survivable while every one of these addresses resolved. It is
 * not survivable now — fleet-backend is about to make `generated/` a reserved prefix in the
 * agreement buckets, so a legacy row still carrying an executed PDF's address will start
 * being refused, and a silent button is the worst possible way to learn that.
 *
 * The sentence names the file and not the reason. Which arm refused — a missing object, a
 * policy, an expired session — is not something a driver can act on, and spelling it out
 * would turn this helper into a probe for what exists in a private bucket.
 */
const OPEN_FAILED_MESSAGE =
    'We could not open that document. Please try again, or contact Primelane if it keeps happening.';

/**
 * Opens a storage file in a new tab using a signed URL.
 * Parses the Supabase storage URL, generates a signed URL, and opens it.
 *
 * Passthrough: if the URL is not a Supabase storage URL (e.g. external links),
 * it is opened directly without signing.
 *
 * ── THIS IS THE PAPER-SCAN ROUTE, AND ONLY THAT ───────────────────────────────
 * An agreement SIGNED IN THE APP is read through `/agreement/:documentId`, which paints the
 * bytes and mints no address at all. What still comes through here is the admin-recorded
 * artefact: a scan of something signed on paper, filed at an ordinary object name. The
 * inconsistency is real and was accepted deliberately — a paper signer can save their scan
 * and an app signer cannot — because the scan is a photograph of a document its signer
 * already holds.
 */
export async function openSignedUrl(storageUrl: string | null | undefined): Promise<void> {
    if (!storageUrl) return;

    // Not a storage URL — open directly
    if (!storageUrl.includes('/storage/v1/object/')) {
        window.open(storageUrl, '_blank');
        return;
    }

    const parsed = parseStorageUrl(storageUrl);
    if (!parsed) {
        window.open(storageUrl, '_blank');
        return;
    }

    const { data, error } = await supabase.storage
        .from(parsed.bucket)
        .createSignedUrl(parsed.path, 3600);

    if (error || !data?.signedUrl) {
        captureError(error ?? new Error('Failed to get signed URL'));
        toast.error(OPEN_FAILED_MESSAGE);
        return;
    }

    window.open(data.signedUrl, '_blank');
}
